/** Attribute marking the feedback UI itself so it never appears in a capture. */
export const FEEDBACK_UI_ATTRIBUTE = 'data-rose-feedback';

const HIGHLIGHT_COLOR = '#ff2d55';
const JPEG_QUALITY = 0.82;

export interface HighlightRect {
    top: number;
    left: number;
    width: number;
    height: number;
}

function drawHighlight(canvas: HTMLCanvasElement, rect: HighlightRect): void {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Dim everything outside the selected element, then outline it.
    ctx.save();
    ctx.fillStyle = 'rgba(0, 0, 0, 0.45)';
    ctx.beginPath();
    ctx.rect(0, 0, canvas.width, canvas.height);
    ctx.rect(rect.left, rect.top, rect.width, rect.height);
    ctx.fill('evenodd');
    ctx.restore();

    ctx.save();
    ctx.strokeStyle = HIGHLIGHT_COLOR;
    ctx.lineWidth = 3;
    ctx.strokeRect(rect.left - 1.5, rect.top - 1.5, rect.width + 3, rect.height + 3);
    ctx.restore();
}

/**
 * Capture the visible viewport as a JPEG data URL.
 *
 * The feedback UI is excluded. When `highlight` is given, the rest of the page
 * is dimmed and the rect is outlined so the reviewer's target is unambiguous.
 */
export async function captureViewport(highlight?: HighlightRect | null): Promise<string> {
    // modern-screenshot rasterises through an SVG foreignObject, so the browser
    // renders the CSS itself. html2canvas re-implements a CSS parser that only
    // knows rgb/hsl and throws on the oklch() design tokens this app is built on.
    const { domToCanvas } = await import('modern-screenshot');

    // ponytail: renders the whole document then crops to the viewport. Simple and
    // exact; if long pages get slow, clone-and-offset only the visible subtree.
    const full = await domToCanvas(document.documentElement, {
        scale: 1,
        filter: (node) => !(node instanceof Element && node.hasAttribute(FEEDBACK_UI_ATTRIBUTE)),
    });

    const canvas = document.createElement('canvas');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not get a 2D canvas context');
    ctx.drawImage(full, -window.scrollX, -window.scrollY);

    if (highlight) {
        drawHighlight(canvas, highlight);
    }

    return canvas.toDataURL('image/jpeg', JPEG_QUALITY);
}

/** Strip the `data:<mime>;base64,` prefix from a data URL. */
export function dataUrlToBase64(dataUrl: string): string {
    return dataUrl.slice(dataUrl.indexOf(',') + 1);
}
